<template>
  <v-dialog v-model="dialogOpen" max-width="480" persistent>
    <div class="wage-slip-broadcast-dialog">
      <LoadingOverlay :show="sending" text="발송 중..." />
      <div class="wage-slip-broadcast-dialog__header">
        <span>테스트 임금명세서 발송</span>
        <button
          type="button"
          class="wage-slip-broadcast-dialog__close"
          :disabled="sending"
          aria-label="닫기"
          @click="dismiss"
        >
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>
      <div class="wage-slip-broadcast-dialog__body">
        <p class="wage-slip-broadcast-dialog__ask">테스트 임금명세서를 발송하시겠습니까?</p>
        <p class="wage-slip-broadcast-dialog__lead">
          소속 요양보호사 앱 채팅으로 보냅니다. 서류관리함에도 월별로 보관됩니다.
        </p>
        <div class="wage-slip-broadcast-dialog__month">
          <label for="yunsu-test-slip-month">몇 월분을 보낼까요?</label>
          <select id="yunsu-test-slip-month" v-model="selectedYm" :disabled="sending">
            <option v-for="opt in monthOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>
        <p v-if="errorText" class="wage-slip-broadcast-dialog__error">{{ errorText }}</p>
        <p v-if="resultText" class="wage-slip-broadcast-dialog__result-summary">{{ resultText }}</p>
      </div>
      <div class="wage-slip-broadcast-dialog__footer">
        <v-btn variant="text" class="btn-close" :disabled="sending" @click="dismiss">
          {{ resultText ? '닫기' : '나중에' }}
        </v-btn>
        <v-btn
          v-if="!resultText"
          variant="flat"
          class="btn-save"
          :disabled="sending || !selectedYm"
          @click="submitSend"
        >
          보내기
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import { wageMagamApi } from '@/services/api';

const TEST_LONGTERM_CODE = '90000000011';
const TEST_CENTER_NAME = '이윤수방문요양';

type MemberLike = {
  m_name?: string;
  m_longterm_code?: string;
} | null;

const props = defineProps<{
  member: MemberLike;
  memberCode: string;
  bizGubun: string;
  yearMonth: string;
}>();

const dialogOpen = ref(false);
const asked = ref(false);
const sending = ref(false);
const errorText = ref('');
const resultText = ref('');
const selectedYm = ref('');

const pad2 = (n: number) => String(n).padStart(2, '0');

const monthOptions = computed(() => {
  const list: { value: string; label: string }[] = [];
  let year = 2026;
  let month = 11;
  while (year < 2027 || (year === 2027 && month <= 12)) {
    list.push({
      value: `${year}${pad2(month)}`,
      label: `${year}년 ${month}월`,
    });
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return list;
});

const isTestCenter = (member: MemberLike) => {
  if (!member) return false;
  const name = String(member.m_name || '').replace(/\s/g, '');
  const code = String(member.m_longterm_code || '').trim();
  return name === TEST_CENTER_NAME || code === TEST_LONGTERM_CODE;
};

const pickDefaultYm = () => {
  const current = String(props.yearMonth || '').replace(/\D/g, '').slice(0, 6);
  if (monthOptions.value.some((opt) => opt.value === current)) return current;
  return monthOptions.value[0]?.value || '';
};

const tryOpen = () => {
  if (asked.value || dialogOpen.value) return;
  if (!isTestCenter(props.member)) return;
  if (!props.memberCode || !props.bizGubun) return;
  asked.value = true;
  selectedYm.value = pickDefaultYm();
  errorText.value = '';
  resultText.value = '';
  dialogOpen.value = true;
};

watch(
  () => [props.member, props.memberCode, props.bizGubun, props.yearMonth],
  () => tryOpen(),
  { immediate: true }
);

const dismiss = () => {
  if (sending.value) return;
  dialogOpen.value = false;
};

const submitSend = async () => {
  if (!props.memberCode || !props.bizGubun || !selectedYm.value || sending.value) return;
  sending.value = true;
  errorText.value = '';
  try {
    const previewRes = await wageMagamApi.previewSlipBroadcast({
      mCode: props.memberCode,
      yearMonth: selectedYm.value,
      bizGubun: props.bizGubun,
    });
    if (!previewRes.data?.success) {
      errorText.value = previewRes.data?.message || '발송 대상을 확인하지 못했습니다.';
      return;
    }
    const matched = Array.isArray(previewRes.data.data?.matched) ? previewRes.data.data.matched : [];
    const targetCayIdxs = matched
      .map((row: { cayIdx?: number }) => Number(row.cayIdx))
      .filter((id: number) => Number.isFinite(id));
    if (!targetCayIdxs.length) {
      errorText.value = '해당 월 급여가 있는 소속 요양보호사가 없습니다.';
      return;
    }
    const opt = monthOptions.value.find((row) => row.value === selectedYm.value);
    const res = await wageMagamApi.sendSlipBroadcast({
      mCode: props.memberCode,
      yearMonth: selectedYm.value,
      bizGubun: props.bizGubun,
      title: `${opt?.label || selectedYm.value} 임금명세서`,
      targetCayIdxs,
    });
    if (!res.data?.success) {
      errorText.value = res.data?.message || '임금명세서 발송에 실패했습니다.';
      return;
    }
    const sent = Number(res.data.data?.sent || 0);
    const failed = Number(res.data.data?.failed || 0);
    resultText.value = failed
      ? `${sent}명 발송 · ${failed}명 실패했습니다.`
      : `${sent}명에게 ${opt?.label || ''} 임금명세서를 발송했습니다.`;
  } catch (error: any) {
    errorText.value =
      error.response?.data?.message || error.message || '임금명세서 발송에 실패했습니다.';
  } finally {
    sending.value = false;
  }
};
</script>
